import React from 'react';
import { Button, Result } from 'antd';
import { createLogger } from '../../../../utils/logger';
import './DashboardErrorBoundary.css';

const logger = createLogger('dashboards');

interface DashboardErrorBoundaryProps {
  children: React.ReactNode;
}

interface DashboardErrorBoundaryState {
  hasError: boolean;
}

/**
 * Scopes runtime errors to the dashboard editor. If a section, the canvas or a
 * widget throws during render, the fallback replaces this feature only — the rest
 * of the app keeps running instead of crashing to a blank screen. "Try again"
 * resets the boundary so a transient error can recover without a full reload.
 *
 * It is worth having here and not only in `agent-setup`: this feature grows into
 * a canvas of user-configured widgets fed by user data (SL-1662..SL-1665), which
 * is exactly the shape of code that throws on an unexpected value.
 *
 * No external dependency: the repo has no shared ErrorBoundary and doesn't bundle
 * `react-error-boundary`, so this is a minimal local class component — the same
 * one `AgentSetupErrorBoundary` settled on.
 */
export class DashboardErrorBoundary extends React.Component<
  DashboardErrorBoundaryProps,
  DashboardErrorBoundaryState
> {
  state: DashboardErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): DashboardErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Through the shared logger, not `console.error`: the definition of done in
    // `governance/dev-rules.yml` forbids new console calls, and the logger is
    // what respects the environment's log level.
    logger.error('render error:', error, info.componentStack);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      // i18n is intentionally avoided here: the boundary must render even if the
      // failure came from the i18n/provider tree it wraps.
      return (
        <div className="dashboard-error-boundary" role="alert">
          <Result
            status="warning"
            title="Something went wrong"
            subTitle="This section failed to load. You can try again without losing the rest of the app."
            extra={
              <Button type="primary" onClick={this.handleRetry}>
                Try again
              </Button>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}
